import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  Users,
  Calendar,
  BarChart3,
  Shield,
  ChevronRight,
  Target,
  Award,
  Play,
  Download,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { getTeams, getMatches, getStandings, getPlayerStats, getMvpLeaderboard } from "../lib/api";

const StatCard = ({ icon: Icon, label, value, color, link, delay }) => (
  <Link
    to={link}
    className={`bg-card border border-border rounded-xl p-6 card-hover animate-fade-in opacity-0`}
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
          {label}
        </p>
        <p className="text-4xl font-heading font-black">{value}</p>
      </div>
      <div
        className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}
      >
        <Icon className="w-6 h-6" />
      </div>
    </div>
    <div className="flex items-center gap-1 mt-4 text-sm text-muted-foreground hover:text-primary transition-colors">
      <span>View details</span>
      <ChevronRight className="w-4 h-4" />
    </div>
  </Link>
);

const RecentMatch = ({ match, teams, index }) => {
  const homeTeam = teams.find((t) => t.id === match.home_team_id);
  const awayTeam = teams.find((t) => t.id === match.away_team_id);

  return (
    <div
      className={`bg-card border border-border rounded-xl p-4 animate-fade-in opacity-0`}
      style={{ animationDelay: `${(index + 4) * 50}ms` }}
      data-testid={`recent-match-${match.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {match.match_type === "league" ? "League" : match.match_type}
        </span>
        <span
          className={`text-xs px-2 py-1 rounded-full ${
            match.status === "completed"
              ? "bg-primary/20 text-primary"
              : "bg-accent/20 text-accent"
          }`}
        >
          {match.status}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          {homeTeam?.logo ? (
            <img
              src={homeTeam.logo}
              alt={homeTeam.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium truncate">{homeTeam?.name || "TBD"}</span>
        </div>
        <div className="px-4 text-center">
          <span className="font-heading text-2xl font-black score-display">
            {match.status === "completed"
              ? `${match.home_score} - ${match.away_score}`
              : "VS"}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-1 justify-end">
          <span className="font-medium truncate text-right">
            {awayTeam?.name || "TBD"}
          </span>
          {awayTeam?.logo ? (
            <img
              src={awayTeam.logo}
              alt={awayTeam.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-3 text-center">
        {match.match_date} • {match.match_time}
      </p>
    </div>
  );
};

const TopScorer = ({ player, index }) => (
  <div
    className={`flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/50 transition-colors animate-fade-in opacity-0`}
    style={{ animationDelay: `${(index + 8) * 50}ms` }}
    data-testid={`top-scorer-${player.player_id}`}
  >
    <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary font-bold">
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate">{player.player_name}</p>
      <p className="text-xs text-muted-foreground">{player.team_name}</p>
    </div>
    <div className="text-2xl font-heading font-black text-primary">
      {player.goals}
    </div>
  </div>
);

const MvpPlayer = ({ player, index }) => (
  <div
    className={`flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/50 transition-colors animate-fade-in opacity-0`}
    style={{ animationDelay: `${(index + 12) * 50}ms` }}
    data-testid={`mvp-player-${player.player_id}`}
  >
    <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-500 font-bold">
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate">{player.player_name}</p>
      <p className="text-xs text-muted-foreground">{player.team_name}</p>
    </div>
    <div className="text-2xl font-heading font-black text-amber-500">
      {player.mvp_awards}
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState({
    teams: 0,
    players: 0,
    matches: 0,
    completed: 0,
  });
  const [teams, setTeams] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [mvpLeaders, setMvpLeaders] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamsRes, matchesRes, standingsRes, playerStatsRes, mvpRes] =
          await Promise.all([
            getTeams(),
            getMatches(),
            getStandings(),
            getPlayerStats(),
            getMvpLeaderboard(),
          ]);

        const teamsData = teamsRes.data;
        const matchesData = matchesRes.data;

        setTeams(teamsData);
        setStats({
          teams: teamsData.length,
          players: playerStatsRes.data.length,
          matches: matchesData.length,
          completed: matchesData.filter((m) => m.status === "completed").length,
        });

        // Recent matches (last 3)
        const sorted = [...matchesData].sort(
          (a, b) => new Date(b.match_date) - new Date(a.match_date)
        );
        setRecentMatches(sorted.slice(0, 3));

        // Top scorers
        const scorers = playerStatsRes.data
          .filter((p) => p.goals > 0)
          .sort((a, b) => b.goals - a.goals)
          .slice(0, 5);
        setTopScorers(scorers);

        // MVP Leaders (top 5)
        setMvpLeaders(mvpRes.data.slice(0, 5));

        // Standings
        setStandings(standingsRes.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const videoUrl = "/standings_race.webm";

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-2">
          Tournament overview and quick stats
        </p>
      </div>

      {/* Season Timeline Video Banner */}
      <div className="bg-gradient-to-r from-primary/20 via-amber-500/20 to-primary/20 border border-primary/30 rounded-xl p-4 md:p-6 animate-fade-in" data-testid="admin-video-banner">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <h3 className="font-heading text-lg md:text-xl font-bold uppercase flex items-center justify-center md:justify-start gap-2">
              <Play className="w-5 h-5 text-primary" />
              Season Timeline
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Watch how the standings evolved from Day 1 to Championship Day!
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowVideo(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="admin-watch-timeline-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Watch Video
            </Button>
            <Button variant="outline" asChild data-testid="admin-download-timeline-btn">
              <a href={videoUrl} download="goodwillers_standings_timeline.webm">
                <Download className="w-4 h-4 mr-2" />
                Download
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl bg-card rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold uppercase">Season Standings Timeline</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowVideo(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <video
                src={videoUrl}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: "70vh" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="p-4 border-t border-border flex justify-end">
              <Button variant="outline" asChild>
                <a href={videoUrl} download="goodwillers_standings_timeline.webm">
                  <Download className="w-4 h-4 mr-2" />
                  Download Video
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          icon={Shield}
          label="Teams"
          value={stats.teams}
          color="bg-primary/10 text-primary"
          link="/teams"
          delay={50}
        />
        <StatCard
          icon={Users}
          label="Players"
          value={stats.players}
          color="bg-accent/10 text-accent"
          link="/players"
          delay={100}
        />
        <StatCard
          icon={Calendar}
          label="Matches"
          value={stats.matches}
          color="bg-amber-500/10 text-amber-500"
          link="/matches"
          delay={150}
        />
        <StatCard
          icon={Trophy}
          label="Completed"
          value={stats.completed}
          color="bg-emerald-500/10 text-emerald-500"
          link="/standings"
          delay={200}
        />
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Matches */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold uppercase tracking-wider">
              Recent Matches
            </h2>
            <Link
              to="/dashboard/matches"
              className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
            >
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {recentMatches.length > 0 ? (
            <div className="space-y-4">
              {recentMatches.map((match, i) => (
                <RecentMatch key={match.id} match={match} teams={teams} index={i} />
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No matches scheduled yet</p>
              <Link
                to="/dashboard/matches"
                className="text-primary hover:underline text-sm mt-2 inline-block"
              >
                Schedule a match
              </Link>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* MVP Leaderboard */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-wider">
                  MVP Leaders
                </h3>
              </div>
              <Link
                to="/player-stats"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                View all
              </Link>
            </div>
            {mvpLeaders.length > 0 ? (
              <div className="space-y-2">
                {mvpLeaders.map((player, i) => (
                  <MvpPlayer key={player.player_id} player={player} index={i} />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">
                No MVP awards yet
              </p>
            )}
          </div>

          {/* Top Scorers */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold uppercase tracking-wider">
                Top Scorers
              </h3>
              <Link
                to="/player-stats"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                View all
              </Link>
            </div>
            {topScorers.length > 0 ? (
              <div className="space-y-2">
                {topScorers.map((player, i) => (
                  <TopScorer key={player.player_id} player={player} index={i} />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">
                No goals scored yet
              </p>
            )}
          </div>

          {/* Mini Standings */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold uppercase tracking-wider">
                Standings
              </h3>
              <Link
                to="/standings"
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Full table
              </Link>
            </div>
            {standings.length > 0 ? (
              <div className="space-y-2">
                {standings.map((team, i) => (
                  <div
                    key={team.team_id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <span className="w-6 text-center font-bold text-muted-foreground">
                      {i + 1}
                    </span>
                    <span className="flex-1 truncate">{team.team_name}</span>
                    <span className="font-bold text-primary">{team.points}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">
                No standings data yet
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
